/**
 * gaspSummary — pure helpers behind two Design Pass 2 moments (founder
 * tear-through 2026-07-10):
 *
 *   B1 "the Gasp": when extraction completes after a posted note, the
 *   composer stages a warm reveal ("From one note: 2 appointments, 1
 *   medication change."). gaspPhrase turns the fresh proposals' wire
 *   types into that sentence — friendly category nouns, counted and
 *   pluralized, never wire tokens.
 *
 *   B6 signature motion: a confirmed proposal's destination section
 *   pulses once where visible. proposalDestinationTestId maps a
 *   proposal type onto the record card it lands in (the cards' stable
 *   testids), so ConfirmQueue can announce the landing and the card can
 *   acknowledge it. Unknown types map to null (no pulse, never a crash).
 *
 * Display layer only — nothing here touches the wire vocabulary.
 */

/** proposal_type → the friendly category noun (singular). Unknown types
 *  fall back to the generic noun so a future family still counts. */
const GASP_NOUNS: Record<string, string> = {
  appointment_past: 'appointment',
  appointment_upcoming: 'appointment',
  medication_add: 'medication change',
  medication_change: 'medication change',
  medication_discontinue: 'medication change',
  condition_add: 'condition update',
  condition_change: 'condition update',
  functional_status_change: 'activity update',
  provider_new: 'care team addition',
  contact_change: 'contact update',
  housing_change: 'housing update',
  risk_change: 'risk update',
  hospital_event: 'hospital event',
  directive_change: 'directive update',
  coverage_change: 'insurance update',
  allergy_change: 'allergy update',
};

const GENERIC_NOUN = 'record update';

/** 'appointment' → 'appointments'; 'medication change' → 'medication
 *  changes' (pluralize the LAST word — every noun here ends regularly). */
function plural(noun: string, n: number): string {
  return n === 1 ? noun : `${noun}s`;
}

/**
 * "2 appointments, 1 medication change, 1 activity update" — counts in
 * first-seen order (the queue's own order), so the sentence follows what
 * the CM is about to review. Empty input returns ''.
 */
export function gaspPhrase(proposalTypes: readonly string[]): string {
  const counts = new Map<string, number>();
  for (const t of proposalTypes) {
    const noun = GASP_NOUNS[t] ?? GENERIC_NOUN;
    counts.set(noun, (counts.get(noun) ?? 0) + 1);
  }
  return [...counts.entries()].map(([noun, n]) => `${n} ${plural(noun, n)}`).join(', ');
}

/** proposal_type → the record card testid the confirm lands in (B6).
 *  null = no mapped destination (no pulse). */
const DESTINATION_CARDS: Record<string, string> = {
  medication_add: 'card-medications',
  medication_change: 'card-medications',
  medication_discontinue: 'card-medications',
  condition_add: 'card-conditions',
  condition_change: 'card-conditions',
  appointment_past: 'card-appointments',
  appointment_upcoming: 'card-appointments',
  risk_change: 'card-risk-flags',
  provider_new: 'card-providers',
  contact_change: 'card-contacts',
  hospital_event: 'card-hospital-events',
  directive_change: 'card-legal-instruments',
  coverage_change: 'card-coverage',
  housing_change: 'card-addresses',
  functional_status_change: 'card-functional-status',
  allergy_change: 'card-allergies',
};

export function proposalDestinationTestId(proposalType: string): string | null {
  return DESTINATION_CARDS[proposalType] ?? null;
}

/** The one event name both sides of the B6 motion agree on. */
export const CONFIRM_LANDED_EVENT = 'kith-confirm-landed';

export interface ConfirmLandedDetail {
  cardTestId: string;
}
